import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { startCheckout } from "@/lib/checkout";
import RecipeCard from "@/components/RecipeCard";
import PricingTiers from "@/components/PricingTiers";
import Footer from "@/components/Footer";
import { TodayPlanner } from "@/components/TodayPlanner";
import { ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const MEAL_CATEGORIES = ["Main", "Snack", "Dessert", "Drink"];
const PERSONAL = ["Your recipes", "Favourites", "Saved"];

export default function Feed() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [myRecipes, setMyRecipes] = useState([]);
  const [favourites, setFavourites] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);

  const isPremium = !!(user && user.is_premium);
  const onUpgrade = () => { if (!user) { navigate("/register"); return; } startCheckout(); };

  useEffect(() => {
    api.get("/recipes").then((r) => setRecipes(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) { setMyRecipes([]); setFavourites([]); setBookmarks([]); return; }
    api.get("/me/recipes").then((r) => setMyRecipes(r.data)).catch(() => {});
    api.get("/me/saved").then((r) => setFavourites(r.data)).catch(() => {});
    api.get("/me/bookmarks").then((r) => setBookmarks(r.data)).catch(() => {});
  }, [user]);

  const categories = useMemo(
    () => (user ? ["All", ...PERSONAL, ...MEAL_CATEGORIES] : ["All", ...MEAL_CATEGORIES]),
    [user]
  );

  const filtered = useMemo(() => {
    if (filter === "All") return recipes;
    if (filter === "Your recipes") return myRecipes;
    if (filter === "Favourites") return favourites;
    if (filter === "Saved") return bookmarks;
    return recipes.filter((r) => (r.category || "Main") === filter);
  }, [recipes, filter, myRecipes, favourites, bookmarks]);

  const isPersonal = PERSONAL.includes(filter);

  const deleteRecipe = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/recipes/${r.id}`);
      setMyRecipes((list) => list.filter((x) => x.id !== r.id));
      setRecipes((list) => list.filter((x) => x.id !== r.id));
      toast.success("Recipe deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete recipe"); }
  };
  const homeRecipes = useMemo(() => (isPersonal ? filtered : filtered.slice(0, 24)), [filtered, isPersonal]);

  return (
    <div className="nerd-gradient">
      {/* Hero — only for logged-out visitors */}
      {!user && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 lg:pt-8 pb-16 sm:pb-20">
          <div className="relative rounded-3xl overflow-hidden min-h-[560px] sm:min-h-[620px] lg:min-h-[680px] flex items-end animate-fade-up">
            <video
              data-testid="hero-video"
              autoPlay loop muted playsInline
              className="absolute inset-0 w-full h-full object-cover"
              src="https://customer-assets-cm19k8pv.emergentagent.net/job_smart-kitchen-146/artifacts/udwsdmnh_7e26544fc7d7c8b296796396be44ad9861b629fe849186aa2bf8c94748588eaf.mp4"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/25" />
            <div className="relative z-10 p-6 sm:p-10 lg:p-14 max-w-4xl">
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.95] mb-6 text-white">
                <span className="block">We've got you covered</span>
              </h1>
              <p className="text-lg sm:text-xl lg:text-2xl text-white/90 font-semibold leading-snug mb-8 max-w-2xl">
                Creators × AI helping you eat better.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={onUpgrade} data-testid="hero-free-trial-btn"
                  className="inline-flex items-center justify-center bg-[#f5f5f5] text-nerd-bg px-8 py-3.5 rounded-full text-base font-semibold hover:opacity-90 transition-opacity">
                  Try for free
                </button>
                <Link to="/creator/signup" data-testid="hero-become-creator-btn"
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/40 text-white hover:bg-white/20 pl-4 pr-6 py-3.5 rounded-full text-base font-semibold transition-colors">
                  Become a creator <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Reels grid */}
      <section className={`pb-24 ${user ? "pt-8" : "pt-6"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {user && <TodayPlanner />}
          <div className="mb-6">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Discover your next meal</h2>
          </div>
          <div className="flex flex-wrap gap-2.5 mb-8" data-testid="recipe-filters">
            {categories.map((cat) => {
              const active = filter === cat;
              return (
                <button
                  key={cat}
                  data-testid={`filter-${cat.toLowerCase().replace(/\s|&/g, "-")}`}
                  onClick={() => setFilter(cat)}
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                    active ? "bg-nerd-light text-white" : "bg-black/5 text-nerd-light hover:bg-black/10"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>
        {loading ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-0">
            {Array.from({ length: 18 }).map((_, i) => (
              <Skeleton key={i} className="aspect-[9/16] rounded-none bg-nerd-card" />
            ))}
          </div>
        ) : filtered.length ? (
          <>
            <div data-testid="reels-grid" className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-0">
              {homeRecipes.map((r, i) => (
                <RecipeCard key={r.id} recipe={r} index={i} flat onDelete={filter === "Your recipes" ? deleteRecipe : undefined} />
              ))}
            </div>
            {!isPersonal && (
              <div className="flex justify-center mt-12 px-4">
                <Link
                  to="/browse"
                  data-testid="view-more-btn"
                  className="inline-flex items-center gap-2 bg-nerd-light text-white px-8 py-3.5 rounded-full text-base font-semibold hover:opacity-90 transition-opacity"
                >
                  View more <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </>
        ) : (
          <div className="py-20 text-center text-nerd-light/40 text-sm">
            {filter === "Your recipes" ? "You haven't created any recipes yet."
              : filter === "Favourites" ? "No favourites yet — tap the heart on a recipe."
              : filter === "Saved" ? "Nothing saved yet — tap the bookmark on a recipe."
              : "No recipes in this category yet."}
          </div>
        )}
      </section>

      {/* Subscription tiers — only for logged-out visitors */}
      {!user && (
        <section data-testid="pricing-section" className="border-t border-black/8 bg-nerd-lift/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-4">
                Cook free. Create with AI on Premium.
              </h2>
              <p className="text-nerd-light/55 text-lg">
                Browsing and saving favourites is free forever. Unlock the AI Creative Agent, weekly planner and smart supplies for €8.99/month.
              </p>
            </div>
            <PricingTiers
              isPremium={isPremium}
              isLoggedIn={!!user}
              onPremiumCta={onUpgrade}
              trialEligible={!user?.has_used_trial}
            />
          </div>
        </section>
      )}

      {!user && <Footer />}
    </div>
  );
}
